import React, { useState, useEffect, useCallback, useRef } from 'react';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { getCoordinatesForPostcode } from '../utils/locationUtils';

// API key from environment variables
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

/**
 * RouteMap component that displays a map with markers for the user and practice locations
 */
const RouteMap = ({ originPostcode, practice }) => {
  // Debug logging
  useEffect(() => {
    console.log('RouteMap - originPostcode:', originPostcode);
    console.log('RouteMap - practice:', practice);
    
    if (!originPostcode) {
      console.error('originPostcode is null or undefined');
    }
    
    if (!practice) {
      console.error('practice is null or undefined');
    } else {
      console.log('RouteMap - practice.coords:', practice.coords);
      console.log('RouteMap - practice.address:', practice.address);
    }
  }, [originPostcode, practice]);
  const [originCoords, setOriginCoords] = useState(null);
  const [destinationCoords, setDestinationCoords] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 51.5074, lng: -0.1278 }); // Default to London
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Store refs for markers
  const originMarkerRef = useRef(null);
  const destinationMarkerRef = useRef(null);

  // Get coordinates for user location and use practice coordinates directly
  useEffect(() => {
    const fetchCoordinates = async () => {
      if (!originPostcode || !practice) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Get user coordinates from postcode (now async)
        const origin = await getCoordinatesForPostcode(originPostcode);
        
        // Get practice coordinates directly from the practice object
        let destination = null;
        if (practice.coords && practice.coords.lat && practice.coords.lng) {
          // Use coords property if available
          destination = {
            lat: parseFloat(practice.coords.lat),
            lng: parseFloat(practice.coords.lng)
          };
        } else if (practice.address && practice.address.latitude && practice.address.longitude) {
          // Use address.latitude/longitude if coords not available
          destination = {
            lat: parseFloat(practice.address.latitude),
            lng: parseFloat(practice.address.longitude)
          };
        } else if (practice.address && practice.address.postcode) {
          // If no coordinates available, try to get them from the practice postcode
          destination = await getCoordinatesForPostcode(practice.address.postcode);
        }

        console.log('Origin coordinates:', origin);
        console.log('Destination coordinates:', destination);

        if (origin && destination) {
          setOriginCoords(origin);
          setDestinationCoords(destination);
          
          // Set map center to midpoint between origin and destination
          const midLat = (origin.lat + destination.lat) / 2;
          const midLng = (origin.lng + destination.lng) / 2;
          setMapCenter({ lat: midLat, lng: midLng });
          setError(null);
        } else {
          setError('Could not find coordinates for one or both locations');
        }
      } catch (err) {
        console.error('Error fetching coordinates:', err);
        setError('Error loading map coordinates');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCoordinates();
  }, [originPostcode, practice]);

  // Calculate zoom level based on distance between points
  const calculateZoom = useCallback(() => {
    if (!originCoords || !destinationCoords) return 10;
    
    const R = 6371; // Earth's radius in km
    const dLat = (destinationCoords.lat - originCoords.lat) * Math.PI / 180;
    const dLng = (destinationCoords.lng - originCoords.lng) * Math.PI / 180;
    
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(originCoords.lat * Math.PI / 180) * Math.cos(destinationCoords.lat * Math.PI / 180) * 
      Math.sin(dLng/2) * Math.sin(dLng/2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c; // Distance in km
    
    // Adjust zoom based on distance
    if (distance > 100) return 7;
    if (distance > 50) return 8;
    if (distance > 20) return 9;
    if (distance > 10) return 10;
    if (distance > 5) return 11;
    if (distance > 2) return 12;
    return 13;
  }, [originCoords, destinationCoords]);

  if (error) {
    return <div className="text-red-500 text-sm">{error}</div>;
  }

  // Create marker elements for origin and destination
  const createHomeMarker = () => {
    const markerStyle = {
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      background: '#57de21', // Green background
      border: '2px solid white',
      boxShadow: '0 2px 6px rgba(0, 0, 0, .3)',
      cursor: 'pointer',
      zIndex: 10,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    };
    
    return (
      <div style={markerStyle}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
          <path d="M12 3L4 9v12h5v-7h6v7h5V9z" />
        </svg>
      </div>
    );
  };
  
  const createDestinationMarker = () => {
    const markerStyle = {
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      background: '#4285F4', // Blue background
      border: '2px solid white',
      boxShadow: '0 2px 6px rgba(0, 0, 0, .3)',
      cursor: 'pointer',
      zIndex: 10,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    };
    
    return (
      <div style={markerStyle}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
          <path d="M19 15l-6 6-6-6h4V5h4v10h4z" />
        </svg>
      </div>
    );
  };

  return (
    <div className="w-full h-80 rounded-lg overflow-hidden shadow-md border border-gray-200 mb-6">
      {loading ? (
        <div className="w-full h-full flex items-center justify-center bg-gray-100">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="w-full h-full flex items-center justify-center bg-gray-100">
          <div className="text-red-500 text-sm">{error}</div>
        </div>
      ) : (
        <APIProvider apiKey={API_KEY}>
          <Map
            mapId="route-map"
            defaultZoom={calculateZoom()}
            defaultCenter={mapCenter}
            gestureHandling="cooperative"
            disableDefaultUI={false}
            mapTypeControl={false}
            fullscreenControl={true}
            streetViewControl={false}
            zoomControl={true}
            style={{ width: '100%', height: '100%' }}
          >
            {originCoords && (
              <AdvancedMarker 
                position={originCoords}
                title="Your Location"
              >
                {createHomeMarker()}
              </AdvancedMarker>
            )}
            
            {destinationCoords && (
              <AdvancedMarker 
                position={destinationCoords}
                title="Dental Practice"
              >
                {createDestinationMarker()}
              </AdvancedMarker>
            )}
          </Map>
        </APIProvider>
      )}
    </div>
  );
};

export default RouteMap;
